import React, { createRef, FC, useEffect } from 'react';
import { Asset, EditPencilIcon } from '@clearcover/atlas';
import classNames from 'classnames';

import { TextInput, TextInputProps } from '../Text';
import { initialLockedTextState, lockedTextReducer } from './reducer';
import styles from './styles.module.css';

export type LockedTextInputProps = TextInputProps & {
  onSetValue?: (value: string) => void;
  onEdit?: () => void;
};

/**
 * Text input with explicit rules around its disabled state and editing.
 * Users must click a button to enable editing and must edit the entire string.
 * Changing just one character does not work, the field must clear in order to edit
 */
export const LockedTextInput: FC<LockedTextInputProps> = ({
  value,
  className,
  disabled,
  onBlur,
  onSetValue,
  onEdit,
  ...restOfProps
}) => {
  const inputRef = createRef<HTMLInputElement>();
  const [state, dispatch] = React.useReducer(
    lockedTextReducer,
    initialLockedTextState
  );

  useEffect(() => {
    if (!value && !state.locked) {
      dispatch({ variant: 'setLocked', value: true });
    }
  }, [state.locked, value]);

  useEffect(() => {
    if (state.shouldFocus && inputRef.current !== document.activeElement) {
      inputRef.current?.select();
      dispatch({ variant: 'setShouldFocus', value: false });
    }
  }, [inputRef, state.shouldFocus]);

  return (
    <TextInput
      {...restOfProps}
      value={value}
      inputRef={inputRef as React.RefObject<HTMLInputElement>}
      className={classNames(styles.input, className)}
      autoComplete="new-password"
      onKeyDown={(e) => {
        if (state.dirty) {
          return;
        } else if (e.key === '*') {
          e.preventDefault();
        } else if (['Tab', 'Backspace'].includes(e.key) || e.key.length === 1) {
          dispatch({ variant: 'onInitialChange' });
        } else {
          e.preventDefault();
        }
      }}
      onFocus={() => {
        dispatch({
          variant: 'onFocus',
          originalValue: `${value ?? ''}`,
        });
      }}
      onClick={() => {
        if (!state.dirty) {
          onSetValue?.('');
        }
      }}
      onBlur={(e) => {
        if (!state.dirty || !e.target.value) {
          onSetValue?.(state.originalValue || '');
        }
        dispatch({ variant: 'onBlur' });
        onBlur?.(e);
      }}
      disabled={!state.locked || disabled}
    >
      {/* TODO: Handle positioning for various size variants */}
      {state.locked || disabled ? null : (
        <button
          data-testid="locked-text-edit-button"
          className={styles.editButton}
          onClick={(e) => {
            e.preventDefault();
            dispatch({ variant: 'onEditClick' });
            onEdit?.();
          }}
        >
          <Asset
            icon={EditPencilIcon}
            height={20}
            width={20}
            description="edit"
          />
        </button>
      )}
    </TextInput>
  );
};
